import React from 'react';
import styled from 'styled-components';
import { isGlobalProductList } from '../../utils';

const getProductsArray = (variantsId) => {
  let productsArray: string[] = [];
  Object.keys(variantsId).forEach((variantId) => {
    for (let index = 0; index < variantsId[variantId]; index++) {
      productsArray = [...productsArray, variantId];
    }
  });
  return productsArray;
};

const getProductId = (
  variantId,
  productsInCollection: GlobalVariant[] | GlobalProduct[],
) => {
  if (!isGlobalProductList(productsInCollection)) {
    return (
      productsInCollection
        .find(({ id }) => id === +variantId)
        ?.productId.toString() || ''
    );
  } else {
    return (
      productsInCollection
        .flatMap(({ variants }) => variants)
        .find(({ id }) => id === +variantId)
        ?.productId.toString() || ''
    );
  }
};

const getProductImage = (
  variantId,
  productsInCollection: GlobalVariant[] | GlobalProduct[],
) => {
  let variantImage;
  if (!isGlobalProductList(productsInCollection)) {
    variantImage = productsInCollection.find(({ id }) => id === +variantId)
      ?.image;
  } else {
    variantImage = productsInCollection
      .flatMap(({ variants }) => variants)
      .find(({ id }) => id === +variantId)?.image;
  }

  if (!variantImage) {
    return;
  }
  variantImage += variantImage.includes('?') ? '&width=200' : '?width=200';
  return variantImage;
};

interface ProgressBarProps {
  total: number;
  selected: number;
  selectedVariantsId: Record<string, number>;
  isBabWithSectionsContent: boolean;
  selectedProductsBySection: Record<string, Record<string, number>>;
  removeProductFromSection: (sectionId: any, product: GlobalProduct) => void;
  productsInCollection: GlobalVariant[] | GlobalProduct[];
  countLabel: string;
  removeProduct: (productId: GlobalProduct | GlobalVariant) => void;
}

const ProgressBar = ({
  total,
  selected,
  selectedVariantsId,
  productsInCollection,
  isBabWithSectionsContent,
  selectedProductsBySection,
  removeProductFromSection,
  removeProduct,
  countLabel,
}: ProgressBarProps): JSX.Element => {
  const productsArray = getProductsArray(selectedVariantsId);

  const handleRemove = (id) => {
    const removedProduct = { id } as GlobalProduct;

    if (isBabWithSectionsContent) {
      Object.keys(selectedProductsBySection || {}).forEach((key) => {
        const selectedProducts = selectedProductsBySection[key];

        const productIndex = Object.keys(selectedProducts).findIndex(
          (productId) => productId === id,
        );

        if (productIndex !== -1) removeProductFromSection(key, removedProduct);
      });
    } else {
      removeProduct(removedProduct);
    }
  };

  return (
    <ProgressBarContainer>
      {[...Array(total).keys()].map((_, i) => {
        const isEmpty = selected === 0 && i === 0;
        return (
          <ProgressBarItem
            key={i}
            selected={selected >= i + 1}
            variantId={productsArray[i]}
            productId={
              productsArray[i] &&
              getProductId(productsArray[i], productsInCollection)
            }
            image={
              productsArray[i] &&
              getProductImage(productsArray[i], productsInCollection)
            }
          >
            <ProductCountLabelContainer
              color={isEmpty ? 'black' : 'white'}
              isEmpty={isEmpty}
            >
              <ProgressBarCountLabel>
                {(selected === i + 1 || isEmpty) && countLabel}
              </ProgressBarCountLabel>
              {selected >= i + 1 && (
                <RemoveButton
                  onClick={() => handleRemove(productsArray[i])}
                ></RemoveButton>
              )}
            </ProductCountLabelContainer>
          </ProgressBarItem>
        );
      })}
    </ProgressBarContainer>
  );
};

const ProgressBarContainer = styled.div.attrs({
  className: 'awtomatic-progress-container',
})`
  display: flex;
  justify-content: space-evenly;
  left: 0;
  width: 100vw;
  /* Hack coming from CreateBundleContainer styles */
  margin-left: -2rem;
`;

const ProgressBarItem = styled.div.attrs<{
  selected: boolean;
  variantId: string;
  productId: string;
  image: string;
}>(
  ({ selected, variantId, productId, image }) =>
    ({
      className: `awtomatic-progress-item ${
        selected
          ? 'awtomatic-progress-item--completed'
          : 'awtomatic-progress-item--pending'
      }${`${variantId ? ` pv-${variantId}` : ''}`}
    ${`${productId ? ` p-${productId}` : ''}`}`,
      style: {
        '--awt-pb-img': `url(${image})`,
      },
    }) as any,
)<{ selected: boolean; variantId: string; productId: string; image: string }>`
  height: 36px;
  width: 100%;
  background-color: ${({ selected }) => (selected ? '#212B36' : '#f1f2f3')};
`;

const ProductCountLabelContainer = styled.div.attrs({
  className: 'awtomatic-progress-product-count-container',
})<{ isEmpty: boolean }>`
  display: flex;
  flex-direction: ${({ isEmpty }) => (isEmpty ? 'row' : 'row-reverse')};
  color: ${({ color }) => color};
  align-items: center;
  height: 36px;
  position: relative;
  padding: 0px 8px;
`;

const RemoveButton = styled.button.attrs({
  className: 'awtomatic-progress-remove-button',
})`
  display: none;
  &::after {
    content: 'x';
  }
`;

const ProgressBarCountLabel = styled.p.attrs({
  className: 'awtomatic-progress-count-label',
})`
  font-size: 14px;
  line-height: 16px;
  margin: 0;
  position: fixed;
`;

export default ProgressBar;
